// PO Lookup — "search a PO and see every transaction against it"
// (Cimon 2026-07-19). Third pill on the Reconciliation page. Type a PO
// ref / vendor / project → pick the PO → one ledger: vendor payments,
// receipts, bank/card statement lines assigned to the PO, and the
// client side (DEP/FIN invoices + the client payments behind them).
//
// READ-ONLY like the rest of Reconciliation — this surface never writes.
// Money derives through buildPoLedger (pure, utils) per Rule 10;
// statement lines load on demand (admin-only RLS — non-admins simply
// get an empty list back).

import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import { buildPoLedger, fmtMoney } from '../utils';
import { rowToCardStatementLine, rowToCardStatement } from '../lib/cardStatements';

const COLOR_TEXT = '#111111';
const COLOR_MUTED = '#888880';
const COLOR_BORDER = '#E0E0DC';
const COLOR_ROW = '#EFEFEC';
const GREEN = '#1A5C38';
const FUCHSIA = '#D4007A';

const head = { padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: COLOR_MUTED, borderBottom: '0.5px solid ' + COLOR_BORDER, whiteSpace: 'nowrap' };
const headRight = Object.assign({}, head, { textAlign: 'right' });
const cell = { padding: '7px 10px', fontSize: 11, color: '#555552', borderBottom: '0.5px solid ' + COLOR_ROW, whiteSpace: 'nowrap' };
const cellRight = Object.assign({}, cell, { textAlign: 'right' });
const mono = { fontFamily: 'monospace' };

const KIND_LABEL = {
  vendor_deposit: 'Vendor deposit invoice', vendor_final: 'Vendor final invoice',
  vendor_full_payment: 'Vendor full payment', vendor_fee: 'Vendor fee bill',
};

function SectionTitle(p) {
  return (
    <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: COLOR_TEXT, margin: '20px 0 6px' }}>
      {p.children}
    </div>
  );
}

export default function PoLedgerSection() {
  const ctx = useAppContext();
  const purchaseOrders = ctx.purchaseOrders || [];
  const [query, setQuery] = useState('');
  const [poId, setPoId] = useState(null);

  const matches = useMemo(function() {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return purchaseOrders.filter(function(po) {
      if (!po || !po.id) return false;
      return String(po.po_ref || '').toLowerCase().indexOf(q) >= 0
        || String(po.vendorName || po.vendor_name || '').toLowerCase().indexOf(q) >= 0
        || String(po.project || '').toLowerCase().indexOf(q) >= 0;
    }).slice(0, 12);
  }, [query, purchaseOrders]);

  const chosen = poId ? purchaseOrders.find(function(po) { return po.id === poId; }) : null;

  const ledger = useMemo(function() {
    if (!chosen) return null;
    return buildPoLedger(chosen, {
      items: ctx.items || [],
      receipts: ctx.receipts || [],
      vendorPayments: ctx.vendorPayments || [],
      invoices: ctx.invoices || [],
      paymentAllocations: ctx.paymentAllocations || [],
      clientPayments: ctx.clientPayments || [],
    });
  }, [chosen, ctx.items, ctx.receipts, ctx.vendorPayments, ctx.invoices, ctx.paymentAllocations, ctx.clientPayments]);

  // Bank/card statement lines assigned to this PO — on-demand, joined
  // to their statements for the account · period label.
  const [bankLines, setBankLines] = useState(null);
  useEffect(function() {
    if (!poId) { setBankLines(null); return; }
    let alive = true;
    (async function() {
      const res = await supabase.from('card_statement_lines').select('*').eq('po_id', poId);
      if (!alive) return;
      if (res.error || !res.data || !res.data.length) { setBankLines([]); return; }
      const lines = res.data.map(rowToCardStatementLine);
      const ids = Array.from(new Set(lines.map(function(l) { return l.statementId; }).filter(Boolean)));
      let stmts = {};
      if (ids.length) {
        const sres = await supabase.from('card_statements')
          .select('id, period, label, filename, uploaded_by, line_count, created_at').in('id', ids);
        if (!sres.error) (sres.data || []).forEach(function(row) {
          const st = rowToCardStatement(row);
          stmts[st.id] = st;
        });
      }
      if (!alive) return;
      setBankLines(lines.map(function(l) {
        const st = stmts[l.statementId];
        return Object.assign({}, l, {
          account: st ? st.label : '', period: st ? st.period : '',
        });
      }).sort(function(a, b) { return String(a.lineDate).localeCompare(String(b.lineDate)); }));
    })();
    return function() { alive = false; };
  }, [poId]);

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <input
          type="text" value={query}
          data-testid="po-ledger-search"
          onChange={function(e) { setQuery(e.target.value); setPoId(null); }}
          placeholder="Search PO number, vendor, or project…"
          autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          style={{ height: 32, width: 340, padding: '0 12px', border: '0.5px solid ' + COLOR_BORDER,
            borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }}
        />
        {query.trim() && !poId ? (
          <div style={{ border: '0.5px solid ' + COLOR_BORDER, borderRadius: 6, marginTop: 6, maxWidth: 480, background: '#fff' }}>
            {matches.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>No PO matches.</div>
            ) : matches.map(function(po) {
              return (
                <div key={po.id}
                  data-testid={'po-ledger-option-' + (po.po_ref || po.id)}
                  onClick={function() { setPoId(po.id); }}
                  style={{ padding: '8px 12px', fontSize: 11, cursor: 'pointer', borderBottom: '0.5px solid ' + COLOR_ROW }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = '#fff'; }}>
                  <span style={Object.assign({ color: COLOR_TEXT, fontWeight: 500 }, mono)}>{po.po_ref || '—'}</span>
                  <span style={{ color: '#555552', marginLeft: 8 }}>{po.vendorName || po.vendor_name || ''}</span>
                  <span style={{ color: COLOR_MUTED, marginLeft: 8 }}>{po.project || ''}</span>
                  <span style={{ color: COLOR_MUTED, marginLeft: 8 }}>{po.status || ''}</span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {!ledger ? (
        <div style={{ padding: '30px 0', color: COLOR_MUTED, fontSize: 12, fontStyle: 'italic' }}>
          Type a PO number (or vendor / project) and pick the PO — every transaction against it
          surfaces here: vendor payments, receipts, bank and card charges, and the client-side
          invoices and payments.
        </div>
      ) : (
        <div data-testid="po-ledger">
          {/* Header strip */}
          <div style={{ border: '0.5px solid ' + COLOR_BORDER, borderRadius: 6, padding: '12px 16px', background: '#fff' }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: COLOR_TEXT }}>
              <span style={mono}>{ledger.po.ref || '—'}</span>
              <span style={{ fontWeight: 400, color: '#555552', marginLeft: 10 }}>{ledger.po.vendor}</span>
              <span style={{ fontWeight: 400, color: COLOR_MUTED, marginLeft: 10 }}>{ledger.po.project} · {ledger.po.status}</span>
            </div>
            <div style={{ display: 'flex', gap: 26, marginTop: 10, flexWrap: 'wrap', fontSize: 11 }}>
              {[
                ['PO total', fmtMoney(ledger.totals.poTotal), COLOR_TEXT],
                ['Vendor paid', fmtMoney(ledger.totals.vendorPaid), ledger.totals.vendorRemaining <= 0.005 ? GREEN : COLOR_TEXT],
                ['Vendor balance', fmtMoney(ledger.totals.vendorRemaining), ledger.totals.vendorRemaining > 0.005 ? FUCHSIA : COLOR_MUTED],
                ['Client billed', fmtMoney(ledger.totals.clientBilled), COLOR_TEXT],
                ['Client paid', fmtMoney(ledger.totals.clientPaid), ledger.totals.clientPaid >= ledger.totals.clientBilled - 0.005 && ledger.totals.clientBilled > 0 ? GREEN : COLOR_TEXT],
              ].map(function(t) {
                return (
                  <div key={t[0]}>
                    <div style={{ fontSize: 9, letterSpacing: '0.07em', textTransform: 'uppercase', color: COLOR_MUTED }}>{t[0]}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: t[2], marginTop: 2 }}>{t[1]}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Vendor payments */}
          <SectionTitle>Vendor payments ({ledger.vendorPayments.length})</SectionTitle>
          {ledger.vendorPayments.length === 0 ? (
            <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>None recorded.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={head}>Date</th><th style={head}>Type</th><th style={headRight}>Amount</th><th style={head}>Reference</th></tr></thead>
              <tbody>{ledger.vendorPayments.map(function(vp) {
                return (
                  <tr key={vp.id}>
                    <td style={cell}>{vp.date || '—'}</td>
                    <td style={Object.assign({}, cell, vp.type === 'Fee' ? { color: COLOR_MUTED } : {})}>{vp.type === 'Fee' ? 'Fee (CC / bank)' : 'Vendor ' + vp.type.toLowerCase()}</td>
                    <td style={cellRight}>{fmtMoney(vp.amount)}</td>
                    <td style={Object.assign({}, cell, { whiteSpace: 'normal', maxWidth: 380, color: COLOR_MUTED })}>{vp.reference}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}

          {/* Receipts */}
          <SectionTitle>Receipts ({ledger.receipts.length})</SectionTitle>
          {ledger.receipts.length === 0 ? (
            <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>None linked.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={head}>Date</th><th style={head}>Kind</th><th style={headRight}>Amount</th><th style={headRight}>CC fee</th><th style={head}>Status</th></tr></thead>
              <tbody>{ledger.receipts.map(function(rc) {
                return (
                  <tr key={rc.id}>
                    <td style={cell}>{rc.date || '—'}</td>
                    <td style={cell}>{KIND_LABEL[rc.kind] || rc.kind}</td>
                    <td style={cellRight}>{fmtMoney(rc.amount)}</td>
                    <td style={Object.assign({}, cellRight, { color: rc.ccFee > 0 ? '#555552' : COLOR_MUTED })}>{rc.ccFee > 0 ? fmtMoney(rc.ccFee) : '—'}</td>
                    <td style={Object.assign({}, cell, { textTransform: 'capitalize' })}>{rc.status}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}

          {/* Bank / card statement lines */}
          <SectionTitle>Bank &amp; card charges ({bankLines === null ? '…' : bankLines.length})</SectionTitle>
          {bankLines === null ? (
            <div style={{ fontSize: 11, color: COLOR_MUTED }}>Loading…</div>
          ) : bankLines.length === 0 ? (
            <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>
              No statement lines assigned to this PO yet — they attach as statements are uploaded and matched.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={head}>Date</th><th style={head}>Account</th><th style={head}>Descriptor</th><th style={headRight}>Amount</th><th style={head}>How matched</th></tr></thead>
              <tbody>{bankLines.map(function(l) {
                return (
                  <tr key={l.id}>
                    <td style={cell}>{l.lineDate || '—'}</td>
                    <td style={cell}>{(l.account || '—') + (l.period ? ' · ' + l.period : '')}</td>
                    <td style={Object.assign({}, cell, { whiteSpace: 'normal', maxWidth: 320 })}>{l.descriptor}</td>
                    <td style={cellRight}>{fmtMoney(Math.abs(l.amount))}</td>
                    <td style={Object.assign({}, cell, { color: COLOR_MUTED })}>{l.matchMethod || (l.matchedReceiptId ? 'receipt' : 'hand-assigned')}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}

          {/* Client side */}
          <SectionTitle>Client invoices ({ledger.invoices.length})</SectionTitle>
          {ledger.invoices.length === 0 ? (
            <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>No DEP/FIN stamped to this PO&rsquo;s items yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={head}>Ref</th><th style={head}>Type</th><th style={headRight}>Billed</th><th style={headRight}>Client paid</th><th style={head}>Status</th></tr></thead>
              <tbody>{ledger.invoices.map(function(ir) {
                return (
                  <tr key={ir.id}>
                    <td style={Object.assign({}, cell, mono, { color: COLOR_TEXT })}>{ir.ref || '—'}</td>
                    <td style={cell}>{ir.type === 'deposit' ? 'Client deposit invoice' : 'Client final invoice'}</td>
                    <td style={cellRight}>{fmtMoney(ir.amountDue)}</td>
                    <td style={Object.assign({}, cellRight, { color: ir.paid >= ir.amountDue - 0.005 && ir.amountDue > 0 ? GREEN : '#555552' })}>{fmtMoney(ir.paid)}</td>
                    <td style={Object.assign({}, cell, { textTransform: 'capitalize' })}>{ir.status}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}

          <SectionTitle>Client payments ({ledger.clientPayments.length})</SectionTitle>
          {ledger.clientPayments.length === 0 ? (
            <div style={{ fontSize: 11, color: COLOR_MUTED, fontStyle: 'italic' }}>None recorded against this PO&rsquo;s invoices.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={head}>Date</th><th style={headRight}>Amount</th><th style={head}>Method</th><th style={head}>On invoice</th></tr></thead>
              <tbody>{ledger.clientPayments.map(function(cp, i) {
                return (
                  <tr key={i}>
                    <td style={cell}>{cp.date || '—'}</td>
                    <td style={cellRight}>{fmtMoney(cp.amount)}</td>
                    <td style={cell}>{cp.method || '—'}</td>
                    <td style={Object.assign({}, cell, mono)}>{cp.invoiceRef}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
